/**
 * Wire types for the background half of "compila tutte le risposte".
 *
 * The synchronous `POST .../compile-all-answers` is unchanged and still lives on forms-engine's
 * `CompilingApiService`. These describe the asynchronous path: `start` queues a run, the global tracker
 * reports it, and `latest` hands the suggestions over whenever the applicant comes back for them.
 */

export type BulkCompileRunStatus =
  | 'Queued'
  | 'Running'
  | 'Completed'
  | 'Failed'
  | 'Cancelled';

/** True once the run will publish nothing further. A cancelled run is as final as a finished one. */
export function isBulkCompileRunTerminal(
  status: BulkCompileRunStatus,
): boolean {
  return (
    status === 'Completed' || status === 'Failed' || status === 'Cancelled'
  );
}

/**
 * One compiled suggestion.
 *
 * Structurally the same shape the synchronous endpoint returns per question — it is literally the same
 * backend record, serialised by the same options — which is what lets forms-engine feed a background
 * result through the code that already applies a synchronous one. Kept here rather than imported
 * because `api-client` may not depend on another library.
 */
export interface BulkCompiledAnswer {
  questionType?: 'Tabular' | 'Descriptive' | null;
  selectedValue?: string | null;
  selectedLabel?: string | null;
  answer?: string | null;
  justification?: string | null;
  /**
   * MultiRadioButton only: one pick per group of the widget, in widget order, `selectedValue` null for
   * a group the documents do not cover. `selectedValue`/`answer` above are null when this is set.
   */
  groups?: BulkCompiledGroupAnswer[] | null;
  /** MultiplierRadioButton only: the quantity for the option `selectedValue` names (an option key). */
  numericValue?: number | null;
}

export interface BulkCompiledGroupAnswer {
  /** The group's `key` — what the stored answer object is keyed by. */
  groupId: string;
  groupLabel?: string | null;
  selectedValue?: string | null;
  selectedLabel?: string | null;
}

export type BulkCompileSkipReason = 'AlreadyAnswered' | 'UnsupportedWidgetType';

/** What a finished batch produced. Identical in shape to the synchronous response body. */
export interface BulkCompileResult {
  compiled: { widgetId: string; answer: BulkCompiledAnswer }[];
  skipped: { widgetId: string; reason: BulkCompileSkipReason }[];
  failed: { widgetId: string; message: string }[];
}

export interface BulkCompileRun {
  runId: string;
  tenderFormAnswerId: string;
  status: BulkCompileRunStatus;
  /** False compiled only the blank questions; true recompiled every compilable one. */
  overwriteExisting: boolean;
  /** Questions settled and planned. Both 0 until the batch has decided what it will compile. */
  completedQuestions: number;
  totalQuestions: number;
  /** Answers produced. Always present, unlike `result`, which the tracker never carries. */
  compiledCount: number;
  /** Why the run failed as a whole. Written for the applicant and shown verbatim. */
  errorMessage?: string | null;
  /**
   * The suggestions, on the endpoints that carry them. Never written to the form by the server:
   * applying them is the applicant's decision, taken in the review dialog.
   */
  result?: BulkCompileResult | null;
  /** The applicant has reviewed it. An acknowledged run is history and is no longer tracked. */
  acknowledged: boolean;
  /** ISO 8601. */
  updatedAt: string;
}

export interface StartBulkCompileRequest {
  overwriteExisting?: boolean;
  /** Per-question instructions, keyed by widget id. Absent widgets get none. */
  userInstructions?: { [widgetId: string]: string };
}
